import { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { LockIcon, LogoMark } from '@/components/icons'
import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/Field'
import { describeError } from '@/lib/errors'
import { useAuthStore } from '@/store/auth'

/**
 * The partners' door. A partner login is created by a JobSlide admin; there is
 * no sign-up here.
 */
export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const status = useAuthStore((state) => state.status)
  const reason = useAuthStore((state) => state.reason)
  const login = useAuthStore((state) => state.login)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [show, setShow] = useState(false)
  const [error, setError] = useState<{ title: string; description?: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const from = (location.state as { from?: string } | null)?.from ?? '/'
  if (status === 'authenticated') return <Navigate to={from} replace />

  const notice =
    error ??
    (reason === 'suspended'
      ? describeError(new ApiError('', 403, 'partner_suspended', null))
      : reason === 'expired'
        ? { title: 'Your session ended', description: 'Sessions last 12 hours. Sign in again to continue.' }
        : null)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await login(email.trim(), password)
      navigate(from, { replace: true })
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) setError({ title: 'That email and password do not match.', description: 'Check for typos, or ask your JobSlide contact to reset your password.' })
      else setError(describeError(caught, 'Could not sign in'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="relative flex min-h-dvh flex-col items-center justify-center overflow-hidden bg-canvas px-4 py-10">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(rgb(var(--ink) / 0.045) 1px, transparent 1px), linear-gradient(90deg, rgb(var(--ink) / 0.045) 1px, transparent 1px)',
          backgroundSize: '36px 36px',
          maskImage: 'radial-gradient(ellipse 55% 50% at 50% 45%, #000 20%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 55% 50% at 50% 45%, #000 20%, transparent 100%)',
        }}
      />
      <div className="relative w-full max-w-[400px] animate-fade-up">
        <div className="mb-7 flex items-center gap-3">
          <LogoMark className="h-10 w-10" />
          <div>
            <p className="text-[18px] font-semibold tracking-tight text-ink">JobSlide</p>
            <p className="label-eyebrow mt-1">Partners</p>
          </div>
        </div>

        <form onSubmit={submit} className="card space-y-4 p-5 sm:p-6" noValidate>
          <div>
            <h1 className="text-[20px] font-semibold tracking-[-0.01em] text-ink">Sign in</h1>
            <p className="mt-1 text-[13.5px] text-ink-muted">Your coupons, the users you onboarded and what you have earned.</p>
          </div>
          <TextField appearance="box" label="Email" type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required autoFocus />
          <TextField
            appearance="box"
            className="pr-16"
            label="Password"
            type={show ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            trailing={
              <button type="button" onClick={() => setShow((value) => !value)} className="px-3.5 text-[12.5px] font-medium text-ink-muted hover:text-ink">
                {show ? 'Hide' : 'Show'}
              </button>
            }
          />
          {notice && (
            <div role="alert" className={error || reason === 'suspended' ? 'rounded-field bg-danger/[0.08] px-3.5 py-2.5 text-[13px] leading-relaxed text-danger' : 'rounded-field bg-info/[0.08] px-3.5 py-2.5 text-[13px] leading-relaxed text-info'}>
              <p className="font-medium">{notice.title}</p>
              {notice.description && <p className="mt-0.5 opacity-90">{notice.description}</p>}
            </div>
          )}
          <Button type="submit" fullWidth loading={busy} disabled={!email.trim() || !password}>
            Sign in
          </Button>
        </form>

        <p className="mt-5 flex items-start gap-2 px-1 text-[12.5px] leading-relaxed text-ink-muted">
          <LockIcon className="mt-px h-4 w-4 shrink-0" />
          <span>
            No account yet? Partner logins are created by JobSlide. <span className="font-medium text-ink-soft">Ask your JobSlide contact for access.</span>
          </span>
        </p>
      </div>
    </div>
  )
}
